import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useCounterSession, CounterMatch } from './useCounterSession';
import './CounterPage.css';

type Stage = 'idle' | 'scanning' | 'preview' | 'done';

const SCANNER_ID = 'counter-qr-reader';

const CounterPage: React.FC = () => {
  const { venue, loading, error: sessionError, request } = useCounterSession();
  const [stage, setStage] = useState<Stage>('idle');
  const [code, setCode] = useState('');
  const [match, setMatch] = useState<CounterMatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [today, setToday] = useState<any[]>([]);
  const scannerRef = useRef<any>(null);

  const loadToday = useCallback(async () => {
    try {
      const data = await request('/today');
      setToday(data.validations || []);
    } catch {
      // El historial es informativo: si falla, la pantalla sigue sirviendo.
    }
  }, [request]);

  useEffect(() => { if (venue) loadToday(); }, [venue, loadToday]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* ya estaba parado */ }
      scannerRef.current = null;
    }
  }, []);

  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const doPreview = useCallback(async (raw: string) => {
    setBusy(true);
    setError(null);
    try {
      const data = await request('/check-in/preview', { method: 'POST', body: { code: raw } });
      setMatch(data.match);
      setCode(raw);
      setStage('preview');
    } catch (err: any) {
      setError(err.message);
      setStage('idle');
    } finally {
      setBusy(false);
    }
  }, [request]);

  const startScanner = useCallback(async () => {
    setError(null);
    setStage('scanning');
    try {
      // Import dinámico: la librería de cámara no entra en el bundle principal
      // de la app, que la usa una sola pantalla.
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded: string) => {
          await stopScanner();
          doPreview(decoded.trim());
        },
        () => { /* cada cuadro sin QR entra acá; no es un error */ }
      );
    } catch {
      setError('No pudimos abrir la cámara. Escribe el código a mano.');
      setStage('idle');
    }
  }, [doPreview, stopScanner]);

  const confirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await request('/check-in/confirm', { method: 'POST', body: { code } });
      setStage('done');
      loadToday();
    } catch (err: any) {
      setError(err.message);
      setStage('idle');
    } finally {
      setBusy(false);
    }
  }, [code, request, loadToday]);

  const reset = useCallback(() => {
    setStage('idle');
    setMatch(null);
    setCode('');
    setError(null);
  }, []);

  if (loading) return <div className="counter-page counter-center">Cargando...</div>;

  if (!venue) {
    return (
      <div className="counter-page counter-center">
        <h1>Mostrador</h1>
        <p>{sessionError || 'Este link no es válido. Pídele al dueño de la cancha un link nuevo.'}</p>
      </div>
    );
  }

  return (
    <div className="counter-page">
      <header className="counter-header">
        <div className="counter-venue">{venue.name}</div>
        <div className="counter-subtitle">Validar partidos</div>
      </header>

      {error && <div className="counter-error" role="alert">{error}</div>}

      {stage === 'idle' && (
        <>
          <button className="counter-primary" onClick={startScanner} disabled={busy}>
            Escanear código
          </button>
          <div className="counter-manual">
            <label htmlFor="counter-code">O escribe el código que te muestran</label>
            <input
              id="counter-code"
              className="counter-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD2345"
              autoComplete="off"
              autoCapitalize="characters"
              maxLength={12}
              inputMode="text"
            />
            <button
              className="counter-secondary"
              onClick={() => doPreview(code)}
              disabled={busy || code.trim().length < 8}
            >
              Buscar partido
            </button>
          </div>
        </>
      )}

      {stage === 'scanning' && (
        <div className="counter-scanner">
          <div id={SCANNER_ID} className="counter-scanner-box" />
          <button className="counter-secondary" onClick={() => { stopScanner(); reset(); }}>
            Cancelar
          </button>
        </div>
      )}

      {stage === 'preview' && match && (
        <div className="counter-card">
          <div className="counter-card-title">{match.location}</div>
          <dl className="counter-details">
            <div><dt>Organiza</dt><dd>{match.organizerName || '—'}</dd></div>
            <div><dt>Horario</dt><dd>{match.time}</dd></div>
            <div><dt>Anotados</dt><dd>{match.playersCount}</dd></div>
          </dl>
          <button className="counter-primary" onClick={confirm} disabled={busy}>
            {busy ? 'Confirmando...' : 'Confirmar'}
          </button>
          <button className="counter-secondary" onClick={reset} disabled={busy}>
            Cancelar
          </button>
        </div>
      )}

      {stage === 'done' && (
        <div className="counter-card counter-success">
          <div className="counter-check">✓</div>
          <div className="counter-card-title">Partido validado</div>
          <button className="counter-primary" onClick={reset}>Validar otro</button>
        </div>
      )}

      <section className="counter-today">
        <h2>Hoy validaste {today.length}</h2>
        <ul>
          {today.map((v) => (
            <li key={v.id}>
              <span>{v.matchTime || '—'}</span>
              <span>{v.organizerName || '—'}</span>
              <span>{v.playersCount} jugadores</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default CounterPage;
